import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmRefundModal } from '@library/components/modals/ConfirmRefundModal/ConfirmRefundModal';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { NumberPanel } from '@library/components/numberPanel/NumberPanel';
import { PATHS } from '@bo-constants/routes';
import { ResolutionDetailsContext } from '@bo-hooks/resolutions/resolutionDetails';
import { ResolutionDetailsStep } from '@bo-components/resolutions/resolutionDetails';
import { useDiscardResolutionMutation } from '@bo-services/resolutionsAPI';

export const ResolutionDetailsPreview = () => {
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const {
    isLoading: isResolutionLoading,
    resolutionDetails,
    resolutionDataFetchError
  } = useContext(ResolutionDetailsContext);

  const { resolutionId } = useParams();
  const navigate = useNavigate();

  const [
    discardResolution,
    {
      isLoading: isDiscardLoading,
      isSuccess: isDiscardSuccess,
      isUninitialized: isDisardUninitialized,
      reset
    }
  ] = useDiscardResolutionMutation();

  const { RESOLUTIONS } = PATHS;

  const {
    estimatedAffectedWagers,
    estimatedRefundCents,
    cvsFileName,
    csvRowCount,
    status
  } = resolutionDetails;

  const handleOpenRefundModal = useCallback(() => {
    setIsRefundModalOpen(true);
  }, []);

  const handleCloseRefundModal = useCallback(() => {
    setIsRefundModalOpen(false);
  }, []);

  const handleOpenDiscardModal = useCallback(() => {
    setIsDiscardModalOpen(true);
  }, []);

  const handleCloseDiscardModal = useCallback(() => {
    if (!isDisardUninitialized) {
      reset();
    }
    setIsDiscardModalOpen(false);
  }, [isDisardUninitialized, reset]);

  const handleDiscardConfirmation = useCallback(async () => {
    await discardResolution({ id: resolutionId || '' }).unwrap();
  }, [discardResolution, resolutionId]);

  useEffect(() => {
    if (isDiscardSuccess) {
      navigate(RESOLUTIONS);
    }
  }, [isDiscardSuccess, navigate, RESOLUTIONS]);

  const isResolutionStepDisabled = status !== 'ready';

  if (resolutionDataFetchError) {
    return <NoResults className="m-0" label="Resolution not found." />;
  }

  if (isResolutionLoading) {
    return (
      <div className="self-center">
        <Loading loadingMessage="Loading Resolution" />
      </div>
    );
  }

  return (
    <ResolutionDetailsStep
      isDisabled={isResolutionStepDisabled}
      onClickContinue={handleOpenRefundModal}
      onClickDiscard={handleOpenDiscardModal}
      primaryButtonText="Process"
    >
      <ConfirmRefundModal
        isRefundModalOpen={isRefundModalOpen}
        onClose={handleCloseRefundModal}
      />
      <ConfirmationModal
        confirmationCopy="Discard"
        handleConfirm={handleDiscardConfirmation}
        isOpen={isDiscardModalOpen}
        isLoading={isDiscardLoading}
        label="Discard refund"
        onCancelModal={handleCloseDiscardModal}
      >
        Are you sure you want to discard this refund?
      </ConfirmationModal>
      <div className="flex w-[600px] flex-col items-start gap-6">
        <div className="flex h-[42px] flex-col items-start self-stretch">
          <p className="text-soClean-100 text-base font-semibold">
            Preview refund impact
          </p>
          <p className="text-soClean-180 text-sm font-medium">
            File uploaded:{' '}
            <span className="text-soClean-100 text-sm font-medium">
              {cvsFileName}
            </span>
          </p>
        </div>
        <div className="text-soClean-100 flex h-[120px] items-start gap-4 self-stretch">
          <NumberPanel field="Number of IDs" quantity={csvRowCount} />
          <NumberPanel
            field="Affected wagers"
            quantity={estimatedAffectedWagers}
          />
          <NumberPanel
            field="Estimated Amount"
            quantity={estimatedRefundCents}
            refund
          />
        </div>
      </div>
    </ResolutionDetailsStep>
  );
};
